/**
 * documentCoreRenderers.tsx — T20.18 Etap 3b.
 *
 * Renderers dla rdzeniowych pól dokumentów klasy bare (commercial / internal).
 * Eksportuje też TextInput, Textarea i SelectInput jako building-blocks dla
 * DataTab'ów. Primitives layoutu (Section, Field) żyją w
 * `@/components/kartoteki/CoreFieldLayout`.
 *
 * Pola rdzeniowe (tabela documents):
 *   title, status, urgency, description, notes, documentDate,
 *   source, externalId, externalSource, rejectionReason
 * Pole read-only: systemNumber (auto-generowany przez backend).
 */

import { useTranslation } from 'react-i18next';
import { DOCUMENT_URGENCIES, STANDARD_DOCUMENT_STATUSES } from '@saas/shared';

import { CoreInput, type CoreInputProps } from '@/components/CoreInput';
import { CORE_INPUT_CLASS_MUTED_DISABLED } from '@/components/coreRendererClasses';
import { Dropdown } from '@/components/Dropdown';

export interface BareDocFormData {
  title: string | null;
  status: string;
  urgency: string;
  description: string | null;
  notes: string | null;
  documentDate: string | null;
  source: string;
  externalId: string | null;
  externalSource: string | null;
  rejectionReason: string | null;
  systemNumber: string | null;
  ownerUserId: string | null;
  /** Kategoria dokumentu (oś `record_categories`, kartoteka_key='documents'). Jedna, opcjonalna. */
  categoryId: string | null;
  documentTypeId?: string | null;
}

/** Pusty stan formularza bare-doc (commercial/internal) — initial state stron create/detail. */
export const EMPTY_BARE_DOC_FORM: BareDocFormData = {
  title: null,
  status: 'draft',
  urgency: 'normal',
  description: null,
  notes: null,
  documentDate: null,
  source: 'manual',
  externalId: null,
  externalSource: null,
  rejectionReason: null,
  systemNumber: null,
  ownerUserId: null,
  categoryId: null,
  documentTypeId: null,
};

// ─── Shared input sub-components ─────────────────────────────────────────────

export const inputClass = CORE_INPUT_CLASS_MUTED_DISABLED;

export function TextInput(props: Omit<CoreInputProps, 'variant'>) {
  return <CoreInput variant="mutedDisabled" {...props} />;
}

export function Textarea({
  value,
  onChange,
  disabled,
  testId,
  rows = 3,
  placeholder,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  testId?: string;
  rows?: number;
  placeholder?: string;
  /** Czerwona ramka bez tekstu — dla pola odrzuconego przez serwer (komunikat żyje w toaście). */
  invalid?: boolean;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      rows={rows}
      placeholder={placeholder}
      data-testid={testId}
      aria-invalid={invalid ? true : undefined}
      className={invalid ? `${inputClass} border-red-500 ring-1 ring-red-500` : inputClass}
    />
  );
}

export function SelectInput({
  value,
  onChange,
  options,
  disabled,
  testId,
  withEmpty = false,
  emptyLabel = '— wybierz —',
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  disabled?: boolean;
  testId?: string;
  withEmpty?: boolean;
  emptyLabel?: string;
  /** Czerwona ramka bez tekstu — dla pola odrzuconego przez serwer (komunikat żyje w toaście). */
  invalid?: boolean;
}) {
  return (
    <Dropdown
      options={[
        ...(withEmpty ? [{ value: '', label: emptyLabel }] : []),
        ...options.map((opt) => ({ value: opt, label: opt })),
      ]}
      value={value}
      onChange={onChange}
      disabled={disabled}
      data-testid={testId}
      className={invalid ? 'border-red-500 ring-1 ring-red-500' : undefined}
    />
  );
}

// ─── Dictionaries ─────────────────────────────────────────────────────────────

// Kody statusu i pilności pochodzą z `@saas/shared` (`STANDARD_DOCUMENT_STATUSES`,
// `DOCUMENT_URGENCIES`) — to samo źródło czytają kolumny i filtry list. Źródło dokumentu
// (`DOC_SOURCES`) zostaje tutaj: nie ma wartości domyślnej ani kolumny z listą kodów.
export const DOC_SOURCES = ['manual', 'import', 'api'] as const;

// ─── StatusSelect sub-component (needs useTranslation hook) ──────────────────

export function StatusSelect({
  value,
  onChange,
  disabled,
  testId,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  testId?: string;
  /** Czerwona ramka bez tekstu — dla pola odrzuconego przez serwer (komunikat żyje w toaście). */
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dropdown
      options={STANDARD_DOCUMENT_STATUSES.map((s) => ({
        value: s,
        label: t(`tenant.documents.shared.statusValues.${s}`, { defaultValue: s }),
      }))}
      value={value}
      onChange={onChange}
      disabled={disabled}
      data-testid={testId}
      className={invalid ? 'border-red-500 ring-1 ring-red-500' : undefined}
    />
  );
}

export function UrgencySelect({
  value,
  onChange,
  disabled,
  testId,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  testId?: string;
  /** Czerwona ramka bez tekstu — dla pola odrzuconego przez serwer (komunikat żyje w toaście). */
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dropdown
      options={DOCUMENT_URGENCIES.map((u) => ({
        value: u,
        label: t(`tenant.documents.shared.urgencyValues.${u}`, { defaultValue: u }),
      }))}
      value={value}
      onChange={onChange}
      disabled={disabled}
      data-testid={testId}
      className={invalid ? 'border-red-500 ring-1 ring-red-500' : undefined}
    />
  );
}

export function SourceSelect({
  value,
  onChange,
  disabled,
  testId,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  testId?: string;
  /** Czerwona ramka bez tekstu — dla pola odrzuconego przez serwer (komunikat żyje w toaście). */
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dropdown
      options={DOC_SOURCES.map((s) => ({
        value: s,
        label: t(`tenant.documents.shared.sourceValues.${s}`, { defaultValue: s }),
      }))}
      value={value}
      onChange={onChange}
      disabled={disabled}
      data-testid={testId}
      className={invalid ? 'border-red-500 ring-1 ring-red-500' : undefined}
    />
  );
}
